<script setup lang="ts">
import convert, { type Unit } from 'convert-units'
import { computed } from 'vue'

import type { Settings } from '~/types/types'
import { useMainStore } from '~/stores'

interface Props {
  value: number
  currentUnit?: string
  unitToImperical?: string
  unitToMetric?: string
  settingsOverride?: Settings
  numberFormat?: {
    maximumFractionDigits: number
    minimumFractionDigits: number
  }
}

const props = withDefaults(defineProps<Props>(), {
  currentUnit: 'm',
  unitToImperical: 'mi',
  unitToMetric: 'km',
  settingsOverride: undefined,
  numberFormat: () => ({
    maximumFractionDigits: 1,
    minimumFractionDigits: 0
  })
})

const mainStore = useMainStore()
const { locale } = useI18n()

const settings = computed<Settings>(() => {
  return (
    props.settingsOverride ??
    mainStore.settings ?? {
      distance: 'metric',
      temperature: 'celsius',
      twelveHourClock: false
    }
  )
})

const unit = computed<string>(() => {
  if (props.currentUnit.toUpperCase() === 'C') {
    return settings.value.temperature === 'celsius'
      ? props.unitToMetric
      : props.unitToImperical
  } else {
    return settings.value.distance === 'metric'
      ? props.unitToMetric
      : props.unitToImperical
  }
})

const formatNumber = (value: number): string => {
  const roundedValue = Number(value.toFixed(1))

  const formatter = new Intl.NumberFormat(locale.value || 'en', props.numberFormat)
  
  return formatter.format(roundedValue)
}

const adaptedValue = computed<string>(() => {
  if (!props.value) {
    return '0'
  }

  const convertedValue = convert(props.value)
    .from(props.currentUnit as Unit)
    .to(unit.value === 'mph' ? 'm/h' : unit.value as Unit) // Special case for mph to work around lib

  return formatNumber(convertedValue)
})
</script>

<template>
  <span>
    <slot :value="adaptedValue" name="value">
      <span>{{ adaptedValue }}</span>
    </slot>
    <slot :unit="unit" name="unit" />
  </span>
</template>
